import { Alert, Grid } from "@mui/material";
import { useContext, useEffect, useRef, useState } from 'react';
import { useLoader } from "../../../../hooks/useLoader";
import { QueryStatus } from "../../../../types";
import { SistemaTipo } from "../../../../types/enum/enum";
import { SeccionFirmas } from "../../components/SeccionFirmas";
import { ContextOperacion, ContextOperacionType } from "../../RefactorLayout";
import SeccionLCSLHPTIDatosOperacion from './components/SeccionLCSLHPTIDatosOperacion';
import { OperacionLCSLHPTI } from "./types/types";
import { useOperacionLCSLHPTI } from "./hooks/useOperacionLCSLHPTI";
import SeccionLCSLHPTITablaValores, { SeccionLCSLHPTITablaValoresHandle } from "./components/SeccionLCSLHPTITablaValores";
import { ConfirmarCambiosSinGuardarDialog } from "./components/ConfirmarCambiosSinGuardarDialog";
import { OperacionLCSLHPTIFields } from "./types/fields";


interface SistemaLCSLHPTIContentProps {
  operacion: OperacionLCSLHPTI
  refetchOperacion: () => void
}

const SistemaLCSLHPTIContent = ({ operacion, refetchOperacion }: SistemaLCSLHPTIContentProps) => {
  const tablaValoresRef = useRef<SeccionLCSLHPTITablaValoresHandle>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const resolverRef = useRef<(puedeContinuar: boolean) => void>();

  const beforeAccion = (): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      if (!tablaValoresRef.current?.hasCambiosSinGuardar()) {
        resolve(true);

return;
      }

      resolverRef.current = resolve;
      setConfirmOpen(true);
    });
  };

  const cerrarDialogoYResolver = (puedeContinuar: boolean) => {
    setConfirmOpen(false);
    resolverRef.current?.(puedeContinuar);
    resolverRef.current = undefined;
  };

  const handleGuardarYContinuar = async () => {
    setGuardando(true);
    const huboExito = await tablaValoresRef.current?.guardarCambios();
    setGuardando(false);
    cerrarDialogoYResolver(!!huboExito);
  };

  return (
    <>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <SeccionLCSLHPTIDatosOperacion
            operacion={operacion}
          />
        </Grid>

        <Grid item xs={12}>
          <SeccionLCSLHPTITablaValores
            ref={tablaValoresRef}
            operacion={operacion}
            codSistemaPrincipal={operacion[OperacionLCSLHPTIFields.CodPais]}
            refetchOperacion={refetchOperacion}
          />
        </Grid>

        <Grid item xs={12}>
          <SeccionFirmas
            codSistema={SistemaTipo.LCSLHPTI}
            idOperacion={operacion.id}
            beforeAccion={beforeAccion}
          />
        </Grid>
      </Grid>

      <ConfirmarCambiosSinGuardarDialog
        open={confirmOpen}
        guardando={guardando}
        onCancelar={() => cerrarDialogoYResolver(false)}
        onContinuarSinGuardar={() => cerrarDialogoYResolver(true)}
        onGuardarYContinuar={handleGuardarYContinuar}
      />
    </>
  )
}

function SistemaLCSLHPTI() {
  const { idOperacion } = useContext<ContextOperacionType>(ContextOperacion);
  const { status, operacion, refetch } = useOperacionLCSLHPTI(idOperacion);
  const { showLoader, hideLoader } = useLoader();

  useEffect(() => {
    if (status === QueryStatus.Loading) {
      showLoader()
    } else {
      hideLoader();
    }
  }, [status]);

  if (!operacion) {
    return null;
  }

  if (status === QueryStatus.Error) {
    return (
      <Alert severity='error'>
        Se produjo un error al cargar la operación.
      </Alert>
    )
  }

  return (
    <SistemaLCSLHPTIContent
      operacion={operacion}
      refetchOperacion={refetch}
    />
  )
}

export default SistemaLCSLHPTI;